import { NotificationService } from 'src/app/shared/services/notification.service';
import { inject, Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { PdfSetting, HelperService } from './../../../_services/helper.service';
import { BaseServices } from '../../kuba.services';
import { TranslateService } from '@ngx-translate/core';
import { Table } from 'primeng/table';
import { Subscription, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { CacheHandlerService } from '../../cached.services';
import { EmployeeContactServices } from '../services/employee-admin.service';

import { jsPDF } from '../../../shared/browser-jspdf';

@Component({
  selector: 'customer-employees',
  templateUrl: 'customer-employees.component.html',
  standalone: false
})
export class CustomerEmployeeComponent implements OnInit {
  private readonly notifications = inject(NotificationService);

  @ViewChild('contactsTable', { static: false }) contactsTable: Table;
  employee: any[] = [];
  selectedEmployees = [];
  businesses: { label: string; value: number }[] = [];
  businessFilter: number = null;
  nameFilter: string = '';
  emailFilter: string = '';
  mobileFilter: string = '';
  departmentFilter: string = '';
  isLoading: boolean = false;
  currentPage: number = 1;
  pageSize: number = 10;
  totalItems: number = 0;
  totalPages: number = 0;
  private subscriptions: Subscription[] = [];
  public additionalData: any;
  public remoteWindow: any;
  exportInProgress: boolean = false;
  private exportDialogTimeout: any = null;

  // Debounce subject for filter changes
  private filterChangeSubject = new Subject<string>();

  // Make BaseServices available in template
  public BaseServices = BaseServices;
  constructor(
    private employeeContactServices: EmployeeContactServices,
    private translate: TranslateService,
    private cacheHandlerService: CacheHandlerService,
    private route: ActivatedRoute
  ) {
    this.subscriptions.push(
      this.translate.stream('FILENAME').subscribe(val => {
        this.additionalData = {
          fileName: val.CUSTOMER_LIST,
          header: 'Employee List',
          businessId: BaseServices.BusinessId || 0,
          cultureInfo: BaseServices.userCultureInfo() || 'en-GB',
          columnNames: [
            { title: 'Employee Name', dataKey: 'Name' },
            { title: 'Category', dataKey: 'Category' },
            { title: 'Business Name', dataKey: 'BusinessName' },
            { title: 'Email', dataKey: 'Email' },
            { title: 'Mobile', dataKey: 'Mobile' }
          ]
        };
      })
    );
  }

  ngOnInit() {
    document.querySelector('body').classList.remove('opened');
    this.initAdditionalData();

    // Setup debounced filter changes
    this.setupFilterDebounce();

    const businessList = this.route.snapshot.data['businessNames'];
    this.businesses = [
      { label: this.translate.instant('SELECT'), value: null }
    ];
    if (businessList && Array.isArray(businessList)) {
      businessList.forEach((b: any) => {
        this.businesses.push({ label: b.Name, value: b.Id });
      });
    }
    this.loadEmployees();
  }

  initAdditionalData() {
    this.additionalData = {
      fileName: 'CUSTOMER_LIST',
      header: 'Employee List',
      businessId: BaseServices.BusinessId || 0,
      cultureInfo: BaseServices.userCultureInfo() || 'en-GB',
      columnNames: [
        { title: 'Employee Name', dataKey: 'Name' },
        { title: 'Department', dataKey: 'DepartmentName' },
        { title: 'Business Name', dataKey: 'BusinessName' },
        { title: 'Email', dataKey: 'Email' },
        { title: 'Mobile', dataKey: 'Mobile' }
      ]
    };
  }

  loadEmployees() {
    if (this.isLoading) return;
    this.isLoading = true;
    const filter = {
      PageNumber: this.currentPage,
      PageSize: this.pageSize,
      SortColumn: 'Name',
      SortDirection: 'ASC',
      Name: this.nameFilter || undefined,
      Email: this.emailFilter || undefined,
      Mobile: this.mobileFilter || undefined,
      Department: this.departmentFilter || undefined,
      BusinessId: this.businessFilter || undefined,
      WithUserOnly: false // All employees
    };
    this.subscriptions.push(
      this.employeeContactServices.getPagedAdminEmployeeList(filter).subscribe(
        result => {
          this.employee = result.Items || [];
          this.totalItems = result.TotalCount || 0;
          this.totalPages = result.TotalPages || 0;
          this.isLoading = false;
        },
        error => {
          this.isLoading = false;
        }
      )
    );
  }

  onPageChange(event: any) {
    this.currentPage = event.first / event.rows + 1;
    this.pageSize = event.rows;
    this.loadEmployees();
  }

  /**
   * Setup debounced filter changes
   */
  private setupFilterDebounce() {
    this.subscriptions.push(
      this.filterChangeSubject
        .pipe(
          debounceTime(800) // 800ms debounce for typing
        )
        .subscribe(() => {
          this.currentPage = 1;
          this.loadEmployees();
        })
    );
  }

  /**
   * Trigger debounced filter change
   */
  private triggerFilterChange() {
    this.filterChangeSubject.next('filter');
  }

  onFilterChange() {
    this.triggerFilterChange();
  }

  onBusinessChanged(e: any, businessDropdown: any) {
    this.businessFilter = e.value;
    this.triggerFilterChange();
  }

  onCategoryChanged(c: any, categoryDropdown: any) {
    if (c.value) {
      this.contactsTable.filter(
        categoryDropdown.selectedOption.label,
        'Category',
        'equals'
      );
    } else {
      this.contactsTable.reset();
    }
  }
  onRoleChanged(e: any, roleDropdown: any) {
    if (e.value) {
      this.contactsTable.filter(
        roleDropdown.selectedOption.value,
        'RoleId',
        'equals'
      );
    } else {
      this.contactsTable.reset();
    }
  }

  exportPdf() {
    let employeeIds = [];
    if (this.selectedEmployees.length > 0) {
      this.selectedEmployees.forEach(element => {
        employeeIds.push(element.Id);
      });

      let pdfSetting = new PdfSetting();
      pdfSetting.date = 'Date:' + HelperService.formatDate(new Date());
      pdfSetting.businessName = '';
      pdfSetting.pageHeader = 'Customer employee details';
      let columns = [
        { title: 'Name', dataKey: 'Name' },
        { title: 'Category', dataKey: 'Category' },
        { title: 'Business Name', dataKey: 'BusinessName' },
        { title: 'UserName', dataKey: 'UserName' },
        { title: 'Mobile', dataKey: 'Mobile' },
        { title: 'RoleName', dataKey: 'RoleName' }
      ];
      HelperService.generatePdf(
        this.selectedEmployees,
        columns,
        'Customer employee list',
        pdfSetting,
        'l'
      );
    } else {
      this.notifications.warn('SELECT_ONE_OR_MORE_EMPLOYEES');
    }
  }
  //   Clicking the Employee name opens the employee in a new window
  openEmployeeInNewWindow(employee: any) {
    if (this.remoteWindow) {
      this.remoteWindow.close();
    }
    this.cacheHandlerService.clearCachedData();
    let businessSetting = {
      BusinessName: employee.BusinessName,
      BusinessLogo: employee.BusinessLogo
    };
    sessionStorage.setItem('RModeBusiness', JSON.stringify(businessSetting));
    sessionStorage.setItem('RModeBusinessId', employee.BusinessId);
    let windowOrigin = window.location.origin;
    let windowControl =
      'location=yes,width=' +
      screen.availWidth +
      ',height=' +
      screen.availHeight +
      ',scrollbars=yes,status=yes';

    let baseURL = BaseServices.userBase.BaseUrl;
    this.remoteWindow = window.open(
      windowOrigin +
        '/' +
        baseURL +
        '/app/business/' +
        employee.businessId +
        '/employee/edit/' +
        employee.Id,
      'Remote',
      windowControl
    )!;
  }

  //   Clicking the Business name opens the business in a new window
  openBusinessInNewWindow(employee: any) {
    if (this.remoteWindow) {
      this.remoteWindow.close();
    }
    this.cacheHandlerService.clearCachedData();
    let businessSetting = {
      BusinessName: employee.BusinessName,
      BusinessLogo: employee.BusinessLogo
    };
    sessionStorage.setItem('RModeBusiness', JSON.stringify(businessSetting));
    sessionStorage.setItem('RModeBusinessId', employee.BusinessId);
    let windowOrigin = window.location.origin;
    let windowControl =
      'location=yes,width=' +
      screen.availWidth +
      ',height=' +
      screen.availHeight +
      ',scrollbars=yes,status=yes';

    let baseURL = BaseServices.userBase.BaseUrl;
    this.remoteWindow = window.open(
      windowOrigin +
        '/' +
        baseURL +
        '/app/business/' +
        employee.businessId +
        '/dashboard',
      'Remote',
      windowControl
    )!;
  }

  exportAllEmployees() {
    // The kuba-export component already handles this automatically
    // Just trigger the export through the existing kuba-export component
    console.log('Export triggered through kuba-export component');
  }

  showExportLoader() {
    // Start a 5s timer to show the dialog only if still loading
    this.exportDialogTimeout = setTimeout(() => {
      this.exportInProgress = true;
    }, 5000);
    this.isLoading = true;
  }
  hideExportLoader() {
    // Always clear the timer
    if (this.exportDialogTimeout) {
      clearTimeout(this.exportDialogTimeout);
      this.exportDialogTimeout = null;
    }
    this.exportInProgress = false;
    this.isLoading = false;
  }

  getAllEmployeesForExport = (): Promise<any[]> => {
    console.log('getAllEmployeesForExport called!');
    const filter = {
      PageNumber: 1,
      PageSize: 100000, // Large number to get all filtered data
      SortColumn: 'Name',
      SortDirection: 'ASC',
      Name: this.nameFilter || undefined,
      Email: this.emailFilter || undefined,
      Mobile: this.mobileFilter || undefined,
      Department: this.departmentFilter || undefined,
      BusinessId: this.businessFilter || undefined,
      WithUserOnly: false // All employees
    };
    return new Promise((resolve, reject) => {
      this.employeeContactServices.getPagedAdminEmployeeList(filter).subscribe({
        next: result => {
          console.log('Export result:', result);
          resolve(result.Items || []);
        },
        error: err => {
          console.error('Export error:', err);
          reject(err);
        }
      });
    });
  };

  exportToCSV(data: any[], filename: string) {
    if (!data || !data.length) return;
    const header = Object.keys(data[0]);
    const csv = [
      header.join(','), // header row first
      ...data.map(row =>
        header.map(fieldName => JSON.stringify(row[fieldName] ?? '')).join(',')
      )
    ].join('\r\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
}
